import { el, btn } from '@/ui/dom';
import { sfxUiTick } from '@/audio/sfx';
import { hapticTick } from '@/ui/haptics';
import { CLUB_COLORS, CLUB_COLOR_COUNT } from '@/social/crewLogic';

/**
 * clubUi.ts — the Racer Dashboard's design language for the RACER CLUB
 * screens (M141, owner: "match the screen and modernized button design
 * aesthetic of the Racer Dashboard"). One place builds:
 *
 *  - the dashboard BUTTON: a tall rounded glass slab with a diagonal sheen,
 *    an icon + label centred, an optional small second line, and a chevron
 *    on the right (AccountScreen's RACER CLUB / INVITE FRIENDS buttons).
 *    Variants follow the M92 colour rule: 'gold' = affirmative (the gold-rim
 *    glass of the dashboard's RACER CLUB button), 'share' = electric blue
 *    (the dashboard's INVITE FRIENDS), 'glass' = neutral (NOT NOW / DONE).
 *    Destructive actions never use these (red ghost + ui/confirm.ts).
 *  - the dashboard SECTION HEAD: a gold caps title between two fading gold
 *    rules (the TROPHY CABINET heading).
 *  - (M142) the round ICON button the hero carries beside the club name
 *    (the blue INVITE circle + the captain's gear), and the CLUB PALETTE
 *    swatch row the helmet AND crest pickers share.
 *
 * Every label renders via textContent.
 */

export const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" d="m9 5 7 7-7 7"/></svg>';

export type RidVariant = 'gold' | 'share' | 'glass';

export interface RidButtonOptions {
  variant: RidVariant;
  label: string;
  /** Inline SVG markup for the leading icon. */
  icon?: string;
  /** A small second line under the label (e.g. "INVITE THE WHOLE CLUB"). */
  sub?: string;
  /** Extra classes (layout hooks). */
  className?: string;
  /** Smaller height for sheets, rows and the landscape-phone layout. */
  small?: boolean;
  /** No chevron (e.g. a button that does not lead anywhere). */
  noChevron?: boolean;
}

export interface RidButton {
  el: HTMLButtonElement;
  /** Swap the label (and the second line) in place. */
  setLabel(label: string, sub?: string): void;
}

export function ridButton(
  parent: HTMLElement,
  o: RidButtonOptions,
  onClick: () => void,
): RidButton {
  const cls = ['btn', 'rid-btn', `rid-${o.variant}`];
  if (o.small) cls.push('rid-sm');
  if (o.sub) cls.push('rid-two');
  if (o.noChevron) cls.push('rid-nochev');
  if (o.className) cls.push(o.className);
  const b = btn('', cls.join(' '), parent, onClick);
  if (o.icon) el('span', 'rid-btn-ic', b).innerHTML = o.icon;
  const text = el('span', 'rid-btn-text', b);
  const label = el('span', 'rid-btn-label', text);
  label.textContent = o.label;
  let subEl: HTMLSpanElement | null = null;
  if (o.sub) {
    subEl = el('span', 'rid-btn-sub', text);
    subEl.textContent = o.sub;
  }
  if (!o.noChevron) el('span', 'rid-btn-chev', b).innerHTML = CHEVRON_SVG;
  return {
    el: b,
    setLabel(next: string, sub?: string): void {
      label.textContent = next;
      if (subEl && sub !== undefined) subEl.textContent = sub;
    },
  };
}

/** The dashboard section head: ── TITLE ── in gold caps. */
export function panelHead(parent: HTMLElement, title: string, className = ''): HTMLDivElement {
  const head = el('div', `club-head${className ? ` ${className}` : ''}`, parent);
  el('span', 'club-head-rule', head);
  el('span', 'club-head-title', head).textContent = title;
  el('span', 'club-head-rule', head);
  return head;
}

/** The dashboard foot line: ── SMALL CAPS NOTE ── (rules hide on phones). */
export function footLine(parent: HTMLElement, text: string): HTMLDivElement {
  const line = el('div', 'rid-foot-line club-foot-line', parent);
  el('span', 'rid-foot-rule', line);
  el('span', 'rid-foot-text', line).textContent = text;
  el('span', 'rid-foot-rule', line);
  return line;
}

/**
 * A round ICON-ONLY button (M142: the hero's blue INVITE circle and the
 * captain's gear). It carries the menu click pair (sfxUiTick + a haptic tick)
 * like every shared button, and an accessible name + tooltip since there is
 * no text on it. The visible disc is styled by `className`; an invisible
 * bleed (.club-circle-btn::before) widens the touch target.
 */
export function circleButton(
  parent: HTMLElement,
  o: { className: string; icon: string; label: string },
  onClick: () => void,
): HTMLButtonElement {
  const b = el('button', `club-circle-btn ${o.className}`, parent);
  b.type = 'button';
  b.innerHTML = o.icon;
  b.setAttribute('aria-label', o.label);
  b.title = o.label;
  b.addEventListener('click', () => {
    sfxUiTick();
    hapticTick();
    onClick();
  });
  return b;
}

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5.5 12.5 4.2 4.2 8.8-9.4" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Relative luminance of a #rrggbb colour (0 black … 1 white). */
export function hexLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const lin = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

/** WCAG contrast ratio between two #rrggbb colours (1 … 21). */
export function contrastRatio(a: string, b: string): number {
  const la = hexLuminance(a);
  const lb = hexLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The check mark's two inks (the CSS .club-swatch / .club-swatch.light). */
export const SWATCH_MARK_LIGHT = '#ffffff';
export const SWATCH_MARK_DARK = '#14161c';

/** Which check mark a swatch wears: whichever ink stands out more against
 *  it (every palette colour clears the 3:1 non-text contrast bar — tested). */
export function swatchNeedsDarkMark(hex: string): boolean {
  return contrastRatio(hex, SWATCH_MARK_DARK) > contrastRatio(hex, SWATCH_MARK_LIGHT);
}

export interface SwatchRow {
  /** The row element (label + swatches). */
  el: HTMLDivElement;
  /** Move the selection ring (the picker repaints through this). */
  set(value: number): void;
}

/**
 * One row of the 24 CLUB PALETTE colours (M142) — the helmet picker's
 * PRIMARY / SECONDARY rows and the crest picker's FIELD / RIM + ICON rows.
 * Round swatches, the selected one wears a gold ring + a check mark; each is
 * a real button (focusable, Enter/Space, an aria-label with the colour's
 * name, a tooltip). The grid wraps on narrow screens (no sideways scroll).
 */
export function colorSwatchRow(
  parent: HTMLElement,
  o: { label: string; value: number; onPick: (value: number) => void; className?: string },
): SwatchRow {
  const row = el('div', `club-swatch-row${o.className ? ` ${o.className}` : ''}`, parent);
  const head = el('div', 'club-swatch-head', row);
  el('span', 'hp-row-label', head).textContent = o.label;
  const name = el('span', 'club-swatch-name', head);
  const grid = el('div', 'club-swatches', row);
  grid.setAttribute('role', 'group');
  grid.setAttribute('aria-label', o.label);
  const swatches: HTMLButtonElement[] = [];
  for (let i = 0; i < CLUB_COLOR_COUNT; i++) {
    const c = CLUB_COLORS[i];
    const s = el('button', 'club-swatch', grid);
    s.type = 'button';
    s.setAttribute('aria-label', c.name);
    s.title = c.name;
    s.style.setProperty('--sw', c.hex);
    if (swatchNeedsDarkMark(c.hex)) s.classList.add('light');
    s.innerHTML = CHECK_SVG;
    s.addEventListener('click', () => {
      sfxUiTick();
      o.onPick(i);
    });
    swatches.push(s);
  }
  const set = (value: number): void => {
    swatches.forEach((s, i) => {
      s.classList.toggle('on', i === value);
      s.setAttribute('aria-pressed', String(i === value));
    });
    name.textContent = CLUB_COLORS[value]?.name ?? '';
  };
  set(o.value);
  return { el: row, set };
}
